import React, { useEffect, useMemo, useCallback, useState } from 'react'
import { GET } from '@/api/axios'
import { Box, Stack, Typography, Chip, InputBase, IconButton } from '@mui/material'
import { useNavigate, useSearchParams, useParams } from 'react-router-dom'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import BoardList from '@components/Board/BoardList'
import BoardListTable from '@components/Board/BoardListTable'
import NoticeView from './NoticeView'
import { useIsMobile } from '@/hooks/Responsive'
import SearchIcon from '@mui/icons-material/Search';
import { fetchMyCourseNoticeList } from '@/api/mypage'

function buildNoticeHref(row, searchParams) {
  const current = Object.fromEntries(searchParams.entries())

  return (
    window.location.pathname +
    '?' +
    new URLSearchParams({
      ...current,
      tab: 'notice',
      id: row.bulletinId,
      bbsType: row?.bbsTypeName === '전체' ? 'vlab' : 'lecture',
    }).toString()
  )
}

export function createNoticeTableColumns({ isMobile, onTitleClick, searchParams }) {
  // 번호 컬럼
  const renderNo = (v, row) => {
    // const noticeYn = String(row.noticeYn ?? '').trim()
    // const vlab = Boolean(row.vlab)
    const bbsTypeName = String(row.bbsTypeName ?? '').trim()

    let label = null
    let labelColor = 'text.primary'

    if (bbsTypeName === '전체') {
      label = 'Vlab'
      labelColor = 'primary.main'
    } else if (bbsTypeName === '공지') {
      label = '중요'
      labelColor = 'primary.main'
    }

    if (label) {
      return (
        <Typography
          fontWeight={600}
          color={labelColor}
          sx={{ textAlign: isMobile ? 'left' : 'center' }}
        >
          {label}
        </Typography>
      )
    }

    return (
      <Typography sx={{ textAlign: isMobile ? 'left' : 'center' }}>
        {v}
      </Typography>
    )
  }

  const renderTitle = (v, row) => {
    const bbsTypeName = String(row.bbsTypeName ?? '').trim()
    const href = buildNoticeHref(row, searchParams)
    let category = '';
    let showChip = false
    let chipColor = ''

    if (bbsTypeName === '공지') {
      category = '공지';
      showChip = true
      chipColor = 'grey'
    } else if (bbsTypeName === '전체') {
      category = '전체';
      showChip = true
      chipColor = 'primary'
    }

    return (
      <Stack
        direction={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'flex-start' : 'center'}
        spacing={1}
      >
        {/* 공지/전체만 칩 표시 */}
        {showChip && (
          <Chip
            label={category}
            variant="outlined"
            color={chipColor}
          />
        )}

        {/* 제목 텍스트 */}
        <Typography
          component="a"
          href={href}
          variant={isMobile ? 'subtitle3' : 'body1'}
          fontWeight={isMobile ? '600' : '400' }
          sx={{
            cursor: 'pointer',
            overflow: isMobile ? 'visible' : 'hidden',
            textOverflow: isMobile ? 'unset' : 'ellipsis',
            whiteSpace: isMobile ? 'normal' : 'nowrap',
            wordBreak: 'keep-all',
            '&:hover': { textDecoration: 'underline', fontWeight: '700' },
          }}
          onClick={(e) => {
            e.preventDefault()
            onTitleClick(row);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              e.stopPropagation()
              onTitleClick(row);
            }
          }}
        >
          {v}
        </Typography>
      </Stack>
    )
  }

  const renderAttachments = (v) => {
    // const hasFile = Array.isArray(v) && v.length > 0
    const hasFile = Number(v) > 0
    if (!hasFile) return undefined 
    
    return (
      <AttachFileIcon
        aria-label="첨부파일 있음"
        sx={{
          color: 'grey.dark',
          verticalAlign: 'middle',
        }}
      />
    )
  }

  return [
    { field: 'rowNo', role: 'no', headerName: '번호', sortable: false, width: '96px', align: 'center', render: renderNo, hideTitleInCard: true },
    { field: 'bulletinTitle', role: 'title', headerName: '제목',     sortable: false, align: 'left',  render: renderTitle, hideTitleInCard: true },
    { field: 'registDttm', role: 'date', headerName: '등록일',   sortable: false, width: '180px', align: 'center', hideTitleInCard: true },
    { field: 'attachFileCnt', role: 'attachment', headerName: '첨부파일', sortable: false, width: '120px', align: 'center', render: renderAttachments, hideTitleInCard: true },
  ]
}

export function NoticeToolbar({ 
  search = '',
  onSearch = () => {},
  total,
  isMobile
}) {
  const [inputValue, setInputValue] = useState(search)

  // Sync inputValue with external search prop (from BoardList's internal state)
  // This is important when initialSearch is provided to BoardList and then BoardList passes it to NoticeToolbar
  useEffect(() => {
    setInputValue(search);
  }, [search]);

  const handleSubmit = () => {
    const keyword = inputValue.trim()
    onSearch(keyword)
  }

  return (
    <Stack
      sx={{
        width: '100%',
        mb: 1.5,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'flex-end',
        flexDirection: isMobile ? 'column' : 'row',
      }}
    >
      {/* 전체 건수 */}
      <Box sx={{ mb: { xs: 1, md: 0 } }}>
        <Typography variant="body2" fontWeight={500}>
          전체{' '}
          <Typography
            component="span"
            variant="body2"
            fontWeight={500}
            color="primary.main"
          >
            {total.toLocaleString()}
          </Typography>
          건
        </Typography>
      </Box>

      {/* 검색창 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          px: 1.5,
          height: '48px',
          width: isMobile ? '100%' : '260px',
        }}
      >
        <InputBase
          placeholder="검색어를 입력해 주세요"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          sx={{
            flex: 1,
            color: 'text.primary',
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit() 
          }}
        />
        <IconButton
          onClick={handleSubmit} 
          sx={{ color: 'grey.dark' }}
        >
          <SearchIcon />
        </IconButton>
      </Box>
    </Stack>
  )
}

export default function NoticeTab() {
  const navigate = useNavigate()
  const { isMobile } = useIsMobile()
  const { id: eduLectureId } = useParams()

  const [searchParams, setSearchParams] = useSearchParams()
  const noticeId = searchParams.get('id')
  const bbsType = searchParams.get('bbsType')
  const searchQuery = searchParams.get('search') || '' // Get search query from URL

  const updateParams = (updates) => {
    const current = Object.fromEntries(searchParams.entries())
    setSearchParams({
      tab: 'notice', // 항상 유지
      ...current,    // 기존 쿼리 유지
      ...updates,    // 새 쿼리 반영
    })
  }

  useEffect(() => {
    const params = Object.fromEntries(searchParams.entries())

    // This useEffect is meant to ensure 'tab: notice' is always in the URL.
    // It should not interfere with other parameters like 'search'.
    if (params.tab === 'notice') return

    setSearchParams({
      ...params,
      tab: 'notice',
    })
  }, [searchParams, setSearchParams]) 

  const handleTitleClick = useCallback((row) => {
    // When clicking a title, we navigate to the notice view, preserving other params like 'search'
    // but overriding 'id' and ensuring 'tab' is 'notice'.
    const current = Object.fromEntries(searchParams.entries());
    setSearchParams({
      ...current,
      tab: 'notice',
      id: row.bulletinId,
      bbsType: row?.bbsTypeName === '전체' ? 'vlab' : 'lecture'
    });
  }, [searchParams, setSearchParams])

  const toNumber = (v) => {
    const n = Number(v)
    return Number.isFinite(n) ? n : NaN
  }

  const fetcher = useCallback(async ({ page, pageSize, sort }) => {
    const response = await fetchMyCourseNoticeList({
      params: { 
        keyword: searchQuery, // This `search` argument comes from BoardList's internal state.
        eduLectureId: eduLectureId, // Temporarily hardcoded as per user's instruction.
        page: page,
        size: pageSize,
      },
    })
    const fetchedItems = response?.data?.content || []
    const total = response?.data?.totalElements || 0

    return {
      items: fetchedItems.map((r) => ({
        ...r,
        category: String(r.category ?? '').trim(),
        vlab: !!r.vlab,
      })),
      total,
    }
  }, [searchQuery])

  const columns = useMemo(
    () => createNoticeTableColumns({ isMobile, onTitleClick: handleTitleClick, searchParams }),
    [isMobile, handleTitleClick, searchParams]
  )

  const toolbarRender = useCallback((ctx) => (
    <NoticeToolbar
      search={ctx.search} // BoardList's internal search state, initialized by initialSearch prop
      onSearch={(v) => {
        ctx.setSearch(v) // Update BoardList's internal search state
        ctx.setPage(1) // Reset page to 1 on new search
        updateParams({ search: v }) // Update URL parameter
      }}
      total={ctx.total}
      isMobile={isMobile} // Pass isMobile to NoticeToolbar
    />
  ), [updateParams, isMobile])

  return (
    <Box>
      {noticeId ? (
        <NoticeView
          id={noticeId}
          bbsType={bbsType}
          onBack={() => {
            const current = Object.fromEntries(searchParams.entries())
            delete current.id
            setSearchParams(current) // Navigating back should remove 'id' from URL, preserving 'search' if present.
          }}
        />
      ) : (
        <BoardList
          view="table"
          columns={columns}
          fetcher={fetcher}
          TableComponent={BoardListTable}
          initialSort={{ field: 'no', direction: 'desc' }}
          initialPage={1}
          initialPageSize={10}
          initialSearch={searchQuery} // Pass initial search query from URL to BoardList
          toolbarRender={toolbarRender}
        //  onRowClick={handleTitleClick}
        />
      )}
    </Box>
  )
}